import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gray-50" }
const _hoisted_2 = { class: "hidden xl:flex items-center gap-4" }
const _hoisted_3 = { class: "xl:hidden bg-white border-b border-gray-200 px-6 py-4" }
const _hoisted_4 = { class: "w-full sm:px-6 py-8 space-y-6" }
const _hoisted_5 = { class: "flex flex-col md:flex-row gap-4 items-center justify-between" }
const _hoisted_6 = { class: "flex-1 max-w-md" }
const _hoisted_7 = { class: "flex flex-wrap items-center gap-4" }
const _hoisted_8 = { class: "bg-white rounded-lg border border-gray-200 shadow-sm p-4" }
const _hoisted_9 = { class: "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2" }
const _hoisted_10 = { class: "flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" }
const _hoisted_11 = { class: "text-blue-600" }
const _hoisted_12 = { class: "text-green-600" }
const _hoisted_13 = { class: "text-red-600" }
const _hoisted_14 = {
  key: 0,
  class: "mt-3 text-blue-600 text-sm"
}
const _hoisted_15 = {
  key: 0,
  class: "flex items-center justify-center py-8 text-black"
}
const _hoisted_16 = {
  key: 1,
  class: "grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
}
const _hoisted_17 = ["id", "checked", "onChange"]
const _hoisted_18 = ["for"]
const _hoisted_19 = {
  key: 0,
  class: "ml-1 text-xs text-red-600"
}
const _hoisted_20 = {
  key: 2,
  class: "text-center py-8 text-black"
}
const _hoisted_21 = ["checked", "disabled"]
const _hoisted_22 = {
  key: 0,
  class: "ml-1 text-xs font-normal text-red-600"
}
const _hoisted_23 = { class: "grid grid-cols-2 lg:grid-cols-3 gap-4" }
const _hoisted_24 = ["id", "checked", "disabled", "onChange"]
const _hoisted_25 = ["for"]
const _hoisted_26 = {
  key: 0,
  class: "ml-1 text-xs text-red-600"
}
const _hoisted_27 = ["checked", "disabled"]
const _hoisted_28 = {
  key: 0,
  class: "ml-1 text-xs font-normal text-red-600"
}
const _hoisted_29 = { class: "mb-6 max-w-md" }
const _hoisted_30 = { class: "grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" }
const _hoisted_31 = ["id", "checked", "disabled", "onChange"]
const _hoisted_32 = ["for"]
const _hoisted_33 = {
  key: 0,
  class: "ml-1 text-xs text-red-600"
}

import { ref, reactive, computed, onMounted } from 'vue'
import { PageHeader, DataCard, BaseInput, ActionButton } from '@/components'
import { getAllCountries, getVendorSectorization, saveSalesSectorization } from '@/api/salesSectorization'
import { useNotification } from '@/composables/useNotification'
import { useAuthStore } from '@/stores/auth'
import { DOM_TOM_REGIONS } from '@/utils/domTom'
import { FRANCE_DEPARTMENTS } from '@/utils/frenchDepartments'
import type { SalesData } from '@/types'
import type { SectorizationCountry } from '@/types/salesSectorization'

const FRANCE_PAYS_ID = 10
const DOM_TOM_PAYS_ID = 42

export default /*@__PURE__*/_defineComponent({
  __name: 'MySalesView',
  setup(__props) {

const SPECIAL_PAYS_IDS = new Set([FRANCE_PAYS_ID, DOM_TOM_PAYS_ID])

const ALLOWED_COUNTRY_CODES = new Set([
  'ZA', 'DZ', 'DE', 'AD', 'AO', 'SA', 'AR', 'AU', 'AT', 'BH', 'BD', 'BB', 'BE', 'BJ', 'BO', 'BA', 'BW', 'BR', 'BG', 'BF', 'BI', 'CM', 'CA', 'CL', 'CY', 'CO', 'KM', 'CG', 'KR', 'CR', 'CI', 'HR', 'DK', 'EG', 'AE', 'EC', 'ES', 'EE', 'US', 'FI', 'GA', 'GE', 'GH', 'GR', 'GN', 'GQ', 'HN', 'HU', 'MU', 'IN', 'IQ', 'IE', 'IS', 'IL', 'IT', 'JE', 'KE', 'XK', 'LV', 'LB', 'LI', 'LT', 'LU', 'MG', 'ML', 'MT', 'MA', 'MR', 'MX', 'MC', 'ME', 'NA', 'NE', 'NG', 'NO', 'NZ', 'PA', 'NL', 'PE', 'PH', 'PL', 'PR', 'PT', 'QA', 'CF', 'CD', 'CZ', 'RO', 'GB', 'RU', 'RW', 'MF', 'SN', 'RS', 'SG', 'SK', 'SI', 'SE', 'CH', 'TW', 'TD', 'TG', 'TT', 'TN', 'TR', 'UA', 'UY', 'VE',
])

const searchQuery = ref('')
const departmentSearch = ref('')
const isLoading = ref(false)
const isAutoSaving = ref(false)
const countries = ref<SectorizationCountry[]>([])
const salesData = reactive<SalesData>({
  selectedCountries: [],
  selectedDomTom: [],
  selectedDepartments: [],
})

const { error } = useNotification()
const authStore = useAuthStore()
const departments = FRANCE_DEPARTMENTS
const domTomRegions = DOM_TOM_REGIONS
const deptCodeSet = new Set(departments.map((d) => d.code))
const domTomCodeSet = new Set(domTomRegions.map((r) => r.code))

const isFranceExcluded = computed(() => salesData.selectedCountries.includes(FRANCE_PAYS_ID))
const isDomTomExcluded = computed(() => salesData.selectedCountries.includes(DOM_TOM_PAYS_ID))
const excludedCountriesCount = computed(
  () => salesData.selectedCountries.filter((id) => !SPECIAL_PAYS_IDS.has(id)).length,
)
const excludedDomTomCount = computed(() =>
  isDomTomExcluded.value ? domTomRegions.length : salesData.selectedDomTom.length,
)
const excludedDepartmentsCount = computed(() =>
  isFranceExcluded.value ? departments.length : salesData.selectedDepartments.length,
)

const filteredCountries = computed(() => {
  const q = searchQuery.value.toLowerCase()
  if (!q) return countries.value
  return countries.value.filter((c) => (c.name ?? '').toLowerCase().includes(q))
})

const filteredDepartments = computed(() => {
  const q = departmentSearch.value.toLowerCase()
  if (!q) return departments
  return departments.filter((d) => d.name.toLowerCase().includes(q) || d.code.includes(departmentSearch.value))
})

const isCountryExcluded = (id: number) => salesData.selectedCountries.includes(id)
const isDomTomCodeExcluded = (code: string) => salesData.selectedDomTom.includes(code)
const isDepartmentExcluded = (code: string) => salesData.selectedDepartments.includes(code)

const zoneItemClass = (isExcluded: boolean, parentExcluded = false) => {
  if (parentExcluded) return 'opacity-50 border-gray-200 bg-gray-50'
  if (isExcluded) return 'border-red-300 bg-red-50'
  return 'border-gray-200 hover:bg-gray-50'
}

const zoneLabelClass = (isExcluded: boolean, parentExcluded = false) => {
  if (parentExcluded) return 'text-gray-500'
  if (isExcluded) return 'text-red-800 font-medium'
  return 'text-black'
}

function toggleInList<T>(list: T[], value: T) {
  const index = list.indexOf(value)
  if (index > -1) list.splice(index, 1)
  else list.push(value)
}

const keptSpecialPays = () =>
  salesData.selectedCountries.filter((id) => SPECIAL_PAYS_IDS.has(id))

const toggleCountry = async (countryId: number) => {
  toggleInList(salesData.selectedCountries, countryId)
  if (salesData.selectedCountries.includes(countryId)) {
    if (countryId === FRANCE_PAYS_ID) salesData.selectedDepartments = []
    if (countryId === DOM_TOM_PAYS_ID) salesData.selectedDomTom = []
  }
  await autoSave()
}

const toggleDomTom = async (code: string) => {
  if (isDomTomExcluded.value) return
  toggleInList(salesData.selectedDomTom, code)
  await autoSave()
}

const toggleDepartment = async (code: string) => {
  if (isFranceExcluded.value) return
  toggleInList(salesData.selectedDepartments, code)
  await autoSave()
}

const selectAllCountries = async () => {
  salesData.selectedCountries = [
    ...new Set([...countries.value.map((c) => c.id_pays), ...keptSpecialPays()]),
  ]
  await autoSave()
}

const deselectAllCountries = async () => {
  salesData.selectedCountries = keptSpecialPays()
  await autoSave()
}

const autoSave = async () => {
  if (isAutoSaving.value || !authStore.isAuthenticated) return
  isAutoSaving.value = true
  try {
    const result = await saveSalesSectorization(
      salesData.selectedCountries,
      salesData.selectedDomTom,
      salesData.selectedDepartments,
    )
    if (!result.success) error('Erreur lors de la sauvegarde automatique')
  } catch {
    error('Erreur lors de la sauvegarde automatique')
  } finally {
    isAutoSaving.value = false
  }
}

const exportData = () => {
  const blob = new Blob(
    [JSON.stringify({
      countries: salesData.selectedCountries,
      domTom: salesData.selectedDomTom,
      departments: salesData.selectedDepartments,
      exportDate: new Date().toISOString(),
    }, null, 2)],
    { type: 'application/json' },
  )
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `sectorisation-${new Date().toISOString().split('T')[0]}.json`
  link.click()
  URL.revokeObjectURL(url)
}

const loadData = async () => {
  isLoading.value = true
  try {
    const [countriesData, vendorData] = await Promise.all([
      getAllCountries(),
      getVendorSectorization(),
    ])

    countries.value = countriesData
      .filter((country) => ALLOWED_COUNTRY_CODES.has(country.code))
      .map((country) => ({
        ...country,
        id_pays: Number(country.id_pays),
        name: country.texte_fr,
      }))

    const allowedPaysIds = new Set([
      ...countries.value.map((c) => c.id_pays),
      FRANCE_PAYS_ID,
      DOM_TOM_PAYS_ID,
    ])
    // Les anciennes sectorisations peuvent être rattachées à une marque.
    // Elles restent des exclusions existantes et doivent donc être visibles ici.
    const secteurs = vendorData.secteurs ?? []

    salesData.selectedCountries = [
      ...new Set(
        secteurs
          .filter((s) => !s.codePostal && allowedPaysIds.has(Number(s.id_pays)))
          .map((s) => Number(s.id_pays)),
      ),
    ]
    salesData.selectedDepartments = [
      ...new Set(
        secteurs
          .filter((s) => Number(s.id_pays) === FRANCE_PAYS_ID && s.codePostal && deptCodeSet.has(String(s.codePostal)))
          .map((s) => String(s.codePostal)),
      ),
    ]
    salesData.selectedDomTom = [
      ...new Set(
        secteurs
          .filter((s) => Number(s.id_pays) === DOM_TOM_PAYS_ID && s.codePostal && domTomCodeSet.has(String(s.codePostal)))
          .map((s) => String(s.codePostal)),
      ),
    ]

    if (isFranceExcluded.value) salesData.selectedDepartments = []
    if (isDomTomExcluded.value) salesData.selectedDomTom = []
  } catch {
    error('Erreur lors du chargement des données. Veuillez réessayer.')
  } finally {
    isLoading.value = false
  }
}

onMounted(loadData)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(PageHeader), {
      title: "Sectoriser vos ventes",
      subtitle: "Cochez les zones où vous ne souhaitez PAS vendre",
      "show-save": true,
      "show-export": true
    }, {
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(ActionButton), {
            label: "Exporter",
            icon: "fas fa-download",
            variant: "secondary",
            onClick: exportData
          })
        ])
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(ActionButton), {
        label: "Exporter",
        icon: "fas fa-download",
        variant: "secondary",
        onClick: exportData
      })
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(_unref(DataCard), {
        title: "Recherche et filtres",
        icon: "fas fa-search",
        "icon-color": "text-blue-600",
        "icon-bg-color": "bg-blue-100",
        class: "w-full"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_unref(BaseInput), {
                modelValue: searchQuery.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                modelModifiers: { trim: true },
                type: "text",
                placeholder: "Rechercher un pays à exclure...",
                icon: "fas fa-search"
              }, null, 8, ["modelValue"])
            ]),
            _createElementVNode("div", _hoisted_7, [
              _createVNode(_unref(ActionButton), {
                label: "Tout exclure",
                icon: "fas fa-ban",
                variant: "secondary",
                onClick: selectAllCountries
              }),
              _createVNode(_unref(ActionButton), {
                label: "Tout réautoriser",
                icon: "fas fa-check",
                variant: "secondary",
                onClick: deselectAllCountries
              }),
              _createVNode(_unref(ActionButton), {
                label: "Recharger",
                icon: "fas fa-sync-alt",
                variant: "secondary",
                loading: isLoading.value,
                onClick: loadData
              }, null, 8, ["loading"])
            ])
          ])
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          _cache[7] || (_cache[7] = _createElementVNode("div", { class: "flex items-center" }, [
            _createElementVNode("i", { class: "fas fa-ban text-red-600 mr-2" }),
            _createElementVNode("span", { class: "text-sm font-medium text-black" }, "Zones exclues :")
          ], -1)),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("span", _hoisted_11, [
              _cache[4] || (_cache[4] = _createElementVNode("i", { class: "fas fa-globe mr-1" }, null, -1)),
              _createTextVNode(_toDisplayString(excludedCountriesCount.value) + " pays", 1)
            ]),
            _createElementVNode("span", _hoisted_12, [
              _cache[5] || (_cache[5] = _createElementVNode("i", { class: "fas fa-flag mr-1" }, null, -1)),
              _createTextVNode(_toDisplayString(excludedDomTomCount.value) + " DOM/TOM", 1)
            ]),
            _createElementVNode("span", _hoisted_13, [
              _cache[6] || (_cache[6] = _createElementVNode("i", { class: "fas fa-map-marker-alt mr-1" }, null, -1)),
              _createTextVNode(_toDisplayString(excludedDepartmentsCount.value) + " départements", 1)
            ])
          ])
        ]),
        (isAutoSaving.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_14, _cache[8] || (_cache[8] = [
              _createElementVNode("i", { class: "fas fa-spinner fa-spin" }, null, -1),
              _createTextVNode(" Sauvegarde... ")
            ])))
          : _createCommentVNode("", true)
      ]),
      _createVNode(_unref(DataCard), {
        title: "Pays à exclure",
        icon: "fas fa-globe-americas",
        "icon-color": "text-blue-600",
        "icon-bg-color": "bg-blue-100",
        class: "w-full"
      }, {
        default: _withCtx(() => [
          (isLoading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_15, _cache[9] || (_cache[9] = [
                _createElementVNode("i", { class: "fas fa-spinner fa-spin mr-3 text-blue-600" }, null, -1),
                _createTextVNode(" Chargement des pays... ")
              ])))
            : (_openBlock(), _createElementBlock("div", _hoisted_16, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredCountries.value, (country) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: country.id_pays,
                    class: _normalizeClass(["flex items-center p-3 border rounded-lg transition-colors", zoneItemClass(isCountryExcluded(country.id_pays))])
                  }, [
                    _createElementVNode("input", {
                      id: 'country-' + country.id_pays,
                      type: "checkbox",
                      class: "w-4 h-4 flex-shrink-0 text-red-600 border-gray-300 rounded focus:ring-red-500",
                      checked: isCountryExcluded(country.id_pays),
                      onChange: ($event: any) => (toggleCountry(country.id_pays))
                    }, null, 40, _hoisted_17),
                    _createElementVNode("label", {
                      for: 'country-' + country.id_pays,
                      class: _normalizeClass(["ml-3 text-sm cursor-pointer flex-1 truncate", zoneLabelClass(isCountryExcluded(country.id_pays))])
                    }, [
                      _createTextVNode(_toDisplayString(country.name) + " ", 1),
                      (isCountryExcluded(country.id_pays))
                        ? (_openBlock(), _createElementBlock("span", _hoisted_19, "(exclu)"))
                        : _createCommentVNode("", true)
                    ], 10, _hoisted_18)
                  ], 2))
                }), 128))
              ])),
          (!isLoading.value && filteredCountries.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_20, " Aucun pays trouvé "))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_unref(DataCard), {
        title: "France DOM/TOM",
        icon: "fas fa-flag",
        "icon-color": "text-green-600",
        "icon-bg-color": "bg-green-100",
        class: "w-full"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            class: _normalizeClass(["flex items-center p-3 mb-4 border rounded-lg transition-colors", zoneItemClass(isDomTomExcluded.value)])
          }, [
            _createElementVNode("input", {
              id: "pays-domtom",
              type: "checkbox",
              class: "w-4 h-4 flex-shrink-0 text-red-600 border-gray-300 rounded focus:ring-red-500",
              checked: isDomTomExcluded.value,
              disabled: isAutoSaving.value,
              onChange: _cache[1] || (_cache[1] = ($event: any) => (toggleCountry(DOM_TOM_PAYS_ID)))
            }, null, 40, _hoisted_21),
            _createElementVNode("label", {
              for: "pays-domtom",
              class: _normalizeClass(["ml-3 text-sm font-medium cursor-pointer", zoneLabelClass(isDomTomExcluded.value)])
            }, [
              _cache[10] || (_cache[10] = _createTextVNode(" France DOM/TOM ")),
              (isDomTomExcluded.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_22, "(exclu)"))
                : _createCommentVNode("", true)
            ], 2)
          ], 2),
          _createElementVNode("div", _hoisted_23, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(domTomRegions), (region) => {
              return (_openBlock(), _createElementBlock("div", {
                key: region.code,
                class: _normalizeClass(["flex items-center p-3 border rounded-lg transition-colors overflow-hidden", zoneItemClass(isDomTomCodeExcluded(region.code), isDomTomExcluded.value)])
              }, [
                _createElementVNode("input", {
                  id: 'domtom-' + region.code,
                  type: "checkbox",
                  class: "w-4 h-4 flex-shrink-0 text-red-600 border-gray-300 rounded focus:ring-red-500",
                  checked: isDomTomExcluded.value || isDomTomCodeExcluded(region.code),
                  disabled: isDomTomExcluded.value || isAutoSaving.value,
                  onChange: ($event: any) => (toggleDomTom(region.code))
                }, null, 40, _hoisted_24),
                _createElementVNode("label", {
                  for: 'domtom-' + region.code,
                  class: _normalizeClass(["ml-3 text-sm cursor-pointer flex-1 break-words", zoneLabelClass(isDomTomCodeExcluded(region.code), isDomTomExcluded.value)])
                }, [
                  _createTextVNode(_toDisplayString(region.name) + " (" + _toDisplayString(region.code) + ") ", 1),
                  (!isDomTomExcluded.value && isDomTomCodeExcluded(region.code))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_26, "(exclu)"))
                    : _createCommentVNode("", true)
                ], 10, _hoisted_25)
              ], 2))
            }), 128))
          ])
        ]),
        _: 1
      }),
      _createVNode(_unref(DataCard), {
        title: "France métropolitaine",
        icon: "fas fa-map-marker-alt",
        "icon-color": "text-red-600",
        "icon-bg-color": "bg-red-100",
        class: "w-full"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            class: _normalizeClass(["flex items-center p-3 mb-4 border rounded-lg transition-colors", zoneItemClass(isFranceExcluded.value)])
          }, [
            _createElementVNode("input", {
              id: "pays-france",
              type: "checkbox",
              class: "w-4 h-4 flex-shrink-0 text-red-600 border-gray-300 rounded focus:ring-red-500",
              checked: isFranceExcluded.value,
              disabled: isAutoSaving.value,
              onChange: _cache[2] || (_cache[2] = ($event: any) => (toggleCountry(FRANCE_PAYS_ID)))
            }, null, 40, _hoisted_27),
            _createElementVNode("label", {
              for: "pays-france",
              class: _normalizeClass(["ml-3 text-sm font-medium cursor-pointer", zoneLabelClass(isFranceExcluded.value)])
            }, [
              _cache[11] || (_cache[11] = _createTextVNode(" France métropolitaine ")),
              (isFranceExcluded.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_28, "(exclu)"))
                : _createCommentVNode("", true)
            ], 2)
          ], 2),
          _createElementVNode("div", _hoisted_29, [
            _createVNode(_unref(BaseInput), {
              modelValue: departmentSearch.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((departmentSearch).value = $event)),
              modelModifiers: { trim: true },
              type: "text",
              placeholder: "Rechercher un département à exclure...",
              icon: "fas fa-search"
            }, null, 8, ["modelValue"])
          ]),
          _createElementVNode("div", _hoisted_30, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredDepartments.value, (dept) => {
              return (_openBlock(), _createElementBlock("div", {
                key: dept.code,
                class: _normalizeClass(["flex items-center p-3 border rounded-lg transition-colors", zoneItemClass(isDepartmentExcluded(dept.code), isFranceExcluded.value)])
              }, [
                _createElementVNode("input", {
                  id: 'dept-' + dept.code,
                  type: "checkbox",
                  class: "w-4 h-4 flex-shrink-0 text-red-600 border-gray-300 rounded focus:ring-red-500",
                  checked: isFranceExcluded.value || isDepartmentExcluded(dept.code),
                  disabled: isFranceExcluded.value || isAutoSaving.value,
                  onChange: ($event: any) => (toggleDepartment(dept.code))
                }, null, 40, _hoisted_31),
                _createElementVNode("label", {
                  for: 'dept-' + dept.code,
                  class: _normalizeClass(["ml-3 text-sm cursor-pointer flex-1", zoneLabelClass(isDepartmentExcluded(dept.code), isFranceExcluded.value)])
                }, [
                  _createTextVNode(_toDisplayString(dept.name) + " (" + _toDisplayString(dept.code) + ") ", 1),
                  (!isFranceExcluded.value && isDepartmentExcluded(dept.code))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_33, "(exclu)"))
                    : _createCommentVNode("", true)
                ], 10, _hoisted_32)
              ], 2))
            }), 128))
          ])
        ]),
        _: 1
      })
    ])
  ]))
}
}

})